// INTADMIN.B2 (#2111) — pure schema-to-display mapping for the pricing panel.
//
// Kept as a standalone pure module (no React, no fetch) for two reasons: the
// preview must reflect UNSAVED edits, so it cannot call GET /api/pricing and
// read the rendered result back; and the branch matrix below is exactly the
// kind of thing that deserves unit tests independent of a rendered component.
//
// ⚠️ DUPLICATION, KNOWINGLY. #2112 implements the same mapping in
// apps/marketing-web/pricing.html to render the public page. Two renderers of
// "how a price reads" can drift. They are deliberately separate because this
// one must work on in-flight edits that have never reached the server. If the
// mapping changes, BOTH must change — grep for `formatPlanPricing` and for
// pricing.html's renderer together.
//
// ⚠️ EVERY ARITHMETIC INPUT IS COERCED (#2111 D3). The admin API already hands
// back JSON numbers (internal-pricing.router.ts's toAdminJson runs a
// null-guarded toNum), so values straight from the fetch are safe. The hazard
// is the panel's own edit state: <input type="number"> yields a STRING, so an
// in-flight edit reaches this function as `"199"`, and `"199" + 39` is
// `"19939"` with no error thrown. Hence `num()` at every boundary rather than
// trusting the declared type.
//
// NULL NEVER BECOMES 0. A null price means "no published price" ("Contact us");
// zero means genuinely free (Individual). The two are deliberately
// distinguishable all the way from migration 0207 to this file — Number(null)
// is 0, so every conversion here is null-guarded.

export interface PlanPricing {
  pricing_model:  string;
  usd_price:      number | string | null;
  inr_price:      number | string | null;
  per_unit_usd:   number | string | null;
  per_unit_inr:   number | string | null;
  per_unit_label: string | null;
  byollm_usd:     number | string | null;
  byollm_inr:     number | string | null;
  byollm_note:    string | null;
  period:         string;
}

export interface PriceLines {
  /** Primary USD line, or 'Contact us' when there is no published price. */
  usd: string;
  /** Second USD line — 'compound' only ("$39 / client / mo"). */
  usdPerUnit: string | null;
  /** Primary INR line, or the unset placeholder. */
  inr: string;
  /** Second INR line — 'compound' only. */
  inrPerUnit: string | null;
  /** BYO-LLM line: the price when there is one, otherwise the note verbatim. */
  byollm: string | null;
  /**
   * Secondary BYO-LLM text, set ONLY when a plan carries both a price and a
   * note. #2148 — the panel now makes that state reachable by a user action
   * (both fields became editable), where under #2111 it was a shape no seed
   * produced. Previously the note was silently dropped whenever a price
   * existed, so an operator filling it in saw their input do nothing.
   */
  byollmNote: string | null;
}

export const INR_UNSET = 'Set via admin';
export const CONTACT_US = 'Contact us';

/**
 * Coerce a value that may be a number, a numeric string (from an <input>), an
 * empty string (a cleared field), or null. Returns null for anything that is
 * not a finite number — deliberately including '' and NaN, so a cleared field
 * reads as "no price" rather than as zero.
 */
export function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Deterministic grouping. 'en-US' is pinned rather than left to the runtime
// locale: the repo has already been bitten by an en-IN default changing
// formatted output under test.
function group(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function usd(n: number): string {
  return `$${group(n)}`;
}

function inr(n: number): string {
  return `₹${group(n)}`;
}

function periodSuffix(period: string): string {
  return period === 'year' ? '/yr' : '/mo';
}

/**
 * Map a plan's stored fields onto the lines the pricing page displays.
 *
 * Branches (INTADMIN.B2 §5):
 *   flat      → base only
 *   per_seat  → base + "/" + per_unit_label + "/mo"   (the rate IS the base)
 *   compound  → base + "/mo base", plus per_unit + "/" + label + "/mo"
 *   byollm price present → shown as the BYO-LLM price
 *   byollm_note present, no price → the note verbatim
 *   base null → "Contact us"
 *   inr null  → INR_UNSET, never 0
 */
export function formatPlanPricing(p: PlanPricing): PriceLines {
  const baseUsd    = num(p.usd_price);
  const baseInr    = num(p.inr_price);
  const unitUsd    = num(p.per_unit_usd);
  const unitInr    = num(p.per_unit_inr);
  const byollmUsd  = num(p.byollm_usd);
  const label      = p.per_unit_label;
  const per        = periodSuffix(p.period);

  // ── USD / INR primary lines ────────────────────────────────────────────
  //
  // "Contact us" whenever there is no published base price. §5 phrases the
  // fallback as "base_price_usd NULL and no byollm_note", which could be read
  // as suppressing it when a note exists — but Enterprise has BOTH (no base
  // price AND "~35-40% off"), and pricing.html publishes "Contact us" for its
  // price alongside that note. Treating the two as independent lines matches
  // the live page; a plan with a note would otherwise lose its price line
  // entirely. Flagged in the #2111 report rather than decided silently.
  let usdLine: string;
  let inrLine: string;
  let usdPerUnit: string | null = null;
  let inrPerUnit: string | null = null;

  if (baseUsd === null) {
    usdLine = CONTACT_US;
  } else if (p.pricing_model === 'per_seat' && label) {
    usdLine = `${usd(baseUsd)} / ${label} ${per}`;
  } else if (p.pricing_model === 'compound') {
    usdLine = `${usd(baseUsd)} ${per} base`;
  } else {
    usdLine = `${usd(baseUsd)} ${per}`;
  }

  if (baseInr === null) {
    inrLine = INR_UNSET;
  } else if (p.pricing_model === 'per_seat' && label) {
    inrLine = `${inr(baseInr)} / ${label} ${per}`;
  } else if (p.pricing_model === 'compound') {
    inrLine = `${inr(baseInr)} ${per} base`;
  } else {
    inrLine = `${inr(baseInr)} ${per}`;
  }

  // ── compound's second line ─────────────────────────────────────────────
  //
  // Without this a compound plan renders as a bare base price and UNDER-QUOTES
  // the customer — Platform Partner would read "$199 /mo" instead of
  // "$199 /mo + $39 / client / mo". 0207 has a CHECK requiring both
  // per_unit_usd and per_unit_label for this model, so both are expected here.
  if (p.pricing_model === 'compound') {
    if (unitUsd !== null && label) usdPerUnit = `${usd(unitUsd)} / ${label} ${per}`;
    if (unitInr !== null && label) inrPerUnit = `${inr(unitInr)} / ${label} ${per}`;
  }

  // ── BYO-LLM line ───────────────────────────────────────────────────────
  //
  // The number wins when present; the note is the fallback for the two tiers
  // whose BYO-LLM terms are not a single figure (Enterprise's percentage range,
  // Platform Partner's "Custom BYOLLM pricing"). D8: if a future row somehow
  // carries both, show the number here and let the caller render the note as
  // secondary text — never blank the cell.
  //
  // #2148 — when BOTH are set the note is now returned as SECONDARY text
  // rather than discarded. #2111 D8 already said the caller should render it
  // that way, but PriceLines carried no field for it, so the caller could not:
  // the note was simply lost whenever a price existed. Invisible while the
  // panel edited only two fields and no seed row carried both — reachable the
  // moment an operator can type into both.
  let byollm: string | null = null;
  let byollmNote: string | null = null;
  if (byollmUsd !== null) {
    byollm = label
      ? `${usd(byollmUsd)} / ${label} with your own LLM`
      : `${usd(byollmUsd)} with your own LLM`;
    // Secondary only when a price already occupies the primary line.
    byollmNote = p.byollm_note || null;
  } else if (p.byollm_note) {
    byollm = p.byollm_note;
  }

  return { usd: usdLine, usdPerUnit, inr: inrLine, inrPerUnit, byollm, byollmNote };
}

/**
 * INR derived from USD at the live rate — DISPLAY ONLY (#2111 D1).
 *
 * This value is never persisted. #2109's GET returns the STORED base_price_inr
 * verbatim and its PUT writes whatever it is sent, so putting this figure into
 * an editable input and saving the row would overwrite a deliberate PPP price
 * with a conversion (Starter ₹1,999 → ₹4,679 at 95.48 INR/USD, a 2.3x jump on a
 * public page). The panel omits inr_price from the payload entirely for
 * live_rate rows.
 *
 * Returns null when either input is unavailable — a missing rate must never
 * become a wrong price.
 */
export function derivedInr(
  usdValue: number | string | null | undefined,
  rate: number | string | null | undefined,
): number | null {
  const u = num(usdValue);
  const r = num(rate);
  if (u === null || r === null) return null;
  return Math.round(u * r);
}

/** Format a derived INR figure for display, or the unset placeholder. */
export function formatDerivedInr(value: number | null): string {
  return value === null ? INR_UNSET : inr(value);
}
